import { Modal, TextInput, Stack, ActionIcon, Tooltip } from "@mantine/core";
import { IconSearch, IconRestore } from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";

interface SearchModalProps {
  opened: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
  placeholder?: string;
  initialValue?: string;
}

export const SearchModal = ({
  opened,
  onClose,
  onSearch,
  placeholder = "Search...",
  initialValue = "",
}: SearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (opened) {
      setSearchQuery(initialValue);
    }
  }, [opened, initialValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
    onClose();
  };

  const handleReset = () => {
    setSearchQuery("");
    onSearch("");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size={400}
      centered
      padding="xl"
      transitionProps={{
        transition: "fade",
        duration: 150,
        exitDuration: 150,
      }}
      overlayProps={{
        opacity: 0.5,
        blur: 2,
      }}
      styles={{
        content: {
          background: `
            radial-gradient(circle at 20% 30%, var(--theme-overlay-20) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, var(--theme-overlay-15) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, var(--theme-overlay-12) 0%, transparent 70%),
            var(--theme-bg-modal)
          `,
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--theme-border)",
        },
        body: {
          background: "transparent",
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            leftSection={<IconSearch size={20} />}
            rightSection={
              <Tooltip label="Clear search" position="left">
                <ActionIcon
                  ref={resetButtonRef}
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={handleReset}
                  tabIndex={0}
                >
                  <IconRestore size={18} />
                </ActionIcon>
              </Tooltip>
            }
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            autoFocus
            size="lg"
            styles={{
              input: {
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid var(--theme-border)",
                color: "white",
                fontSize: "1.125rem",
                "&:focus": {
                  borderColor: "var(--theme-primary)",
                },
              },
            }}
          />
        </Stack>
      </form>
    </Modal>
  );
};
